import {useAuthUser, Loading} from '../components/components_index';
import {useMD, useSaveMD, useImgDescs, usePeople, useReunions, usePrompt, getImgOptions, useMediaUrl} from '../hooks/hooks_index';
import { useState, useEffect } from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import '../styles/EditReunionBio.css';

export default function EditReunionBio () {
  const {loginID} = useAuthUser();
  const {reunionID} = useParams();
  const navigate = useNavigate();
  const {data: bioText, isLoading} = useMD("reunion", reunionID);
  const {data: people, isLoading: peopleLoading, error: peopleError} = usePeople();
  const {data: reunions, isLoading: reunionsLoading, error: reunionsError} = useReunions();
  const {data: image_infos, isLoading: imgLoading, error: imgError} = useImgDescs();
  // const person = people["P000000"];
  const reunion = reunions[reunionID];
  const saveMD = useSaveMD("reunion");

  const [blocks, setBlocks] = useState([]);
  const [leaveNotAllowed, setLeaveNotAllowed] = useState(true);

  const [imagePickerIndex, setImagePickerIndex] = useState(null);
  const [pickerImages, setPickerImages] = useState([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [pickerError, setPickerError] = useState("");
  const [uploadedByFilter, setUploadedByFilter] = useState("all");
  const [sortField, setSortField] = useState("saved_date");
  const [sortDirection, setSortDirection] = useState("desc");

useEffect(() => {
    setBlocks(markdownToBlocks(bioText))
  }, [bioText]);

  const updateBlock = (index, newData) => {
    const newBlocks = [...blocks];
    newBlocks[index] = {...newBlocks[index], ...newData};
    setBlocks(newBlocks);
  };

  const addBlock = (index, type) => {
    const newBlock =
      type === "img"
        ? {type: "img", src: ""}
        : {type, content: ""};

    const newBlocks = [...blocks];
    newBlocks.splice(index + 1, 0, newBlock);
    setBlocks(newBlocks);
  };

  const deleteBlock = (index) => {
    const newBlocks = [...blocks];
    newBlocks.splice(index, 1);
    setBlocks(newBlocks);
  };

  const handleSave = () => {
    const markdown = blocksToMarkdown(blocks);
    saveMD.mutate(
      {
        id: reunionID,
        markdown
      },
      {
        onSuccess: () => {
          setLeaveNotAllowed(false);
          setTimeout(() => {
            navigate(`/reunion/${reunionID}`);
          }, 10);
        }
      }
    );   
  };

  const openImagePicker = (index) => {
    setUploadedByFilter("all");
    setSortField("saved_date");
    setSortDirection("desc");
    setImagePickerIndex(index);
  };

  const closeImagePicker = () => {
    setImagePickerIndex(null);
  };

  const chooseExistingImage = (src, imageInfo = {}) => {
    if (imagePickerIndex === null) return;

    updateBlock(imagePickerIndex, {
      src,
      alt: imageInfo.alt ?? imageInfo.subtext ?? ""
    });

    closeImagePicker();
  };

  const sortedPeopleOptions = Object.entries(people).sort(
    ([, personA], [, personB]) => {
      const nameA = (
        personA.AKA || `${personA.f_name} ${personA.l_name}`
      ).toLowerCase();
      const nameB = (
        personB.AKA || `${personB.f_name} ${personB.l_name}`
      ).toLowerCase();

      return nameA.localeCompare(nameB);
    }
  );

  useEffect(() => {
    if (imagePickerIndex === null) return;

    setPickerLoading(true);
    setPickerError("");

    const params = {};
    if (uploadedByFilter !== "all") {
      params.uploaded_by = uploadedByFilter;
    }
    params.sortByDate = [
      sortField,
      sortDirection
    ];

    getImgOptions(params)
      .then((data) => {
        const filteredData = data.filter(
          image => image.uploaded_by !== "P000000" //&& !image.profilePic
        )
        setPickerImages(filteredData);
        setPickerLoading(false);
      })
      .catch((err) => {
        setPickerError(err.message);
        setPickerLoading(false);
      });
  }, [imagePickerIndex, uploadedByFilter, sortField, sortDirection]);

  useEffect(() => {
    const handleBeforeUnload = (event) => {     
      event.preventDefault();
      event.returnValue =
        "Are you sure you want to leave this page? Any changes will not be saved.";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    }
  }, []);

  usePrompt(
    "Are you sure you want to leave this page? Any changes will not be saved.",
    leaveNotAllowed
  );

  useEffect(() => {
    if (imagePickerIndex !== null) {
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.width = "100%";
    } else {
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
    }

    return () => {
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
    };
  }, [imagePickerIndex]);

  if (imgError || peopleError || reunionsError) return <div>Error</div>;
  if (isLoading || imgLoading || !image_infos || peopleLoading || reunionsLoading || !reunion) return <Loading/>

  return (
    <div className="edit-reunion-bio-page">
      <div className="edit-bio-title">
        <div className="title-text">Edit {reunion.year} Reunion</div>
      </div>
      <div className="editor-container">
        {blocks.map((block, index) => (
          <div key={block.id} className={`md-block ${(block.type === 'img')?"img-block":""}`}>
            {block.type === 'p' && (
              <div className="md-content">
                <p 
                  className="block-p"
                  contentEditable
                  suppressContentEditableWarning
                  // onInput={(e) => updateBlock(index, {content: e.currentTarget.textContent})}
                  onBlur={(e) => updateBlock(index, { content: e.currentTarget.textContent })}
                >
                  {block.content}
                </p>
                <div contentEditable="false" suppressContentEditableWarning className={`block-controls p-buttons`}>
                  <button onClick={() => addBlock(index, "p")}>Add Text</button>
                  <button onClick={() => addBlock(index, "img")}>Add Image</button>
                  <button onClick={() => deleteBlock(index)}>Delete Paragraph</button>
                </div>
              </div>
            )}
            {block.type === 'h2' && (
              <div className="md-content h2-content">
                <div
                  className="block-h2"
                >
                  {block.content}
                </div>
                <div contentEditable="false" suppressContentEditableWarning className={`block-controls h2-buttons`}>
                  <button onClick={() => addBlock(index, "p")}>Add Text</button>
                  <button onClick={() => addBlock(index, "img")}>Add Image</button>
                </div>
              </div>
            )}
            {block.type === "img" && (
              <div className="md-content img-content">
                <div className="bio-image-box">
                  {block.src ? (
                    <>
                      <BlockImg imageSrc={block.src} alt={block.alt || ""} className="bio-image"/>
                      {image_infos[block.src]?.subtext && (
                        <div className="bio-image-subtext">
                          {image_infos[block.src].subtext}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="empty-image-placeholder">No image selected</div>
                  )}
                  <div className="image-hover-overlay">
                    <button
                      type="button"
                      className="change-image-button"
                      onClick={() => openImagePicker(index)}
                    >
                      {block.src ? "Change image" : "Select image"}
                    </button>
                  </div>
                </div>
                <div contentEditable="false" suppressContentEditableWarning className="block-controls img-buttons">
                  <button type="button" onClick={() => addBlock(index, "p")}>Add Text</button>
                  <button type="button" onClick={() => addBlock(index, "img")}>Add Image</button>
                  <button type="button" onClick={() => deleteBlock(index)}>Delete Image</button>
                </div>
              </div>
            )}
          </div>
        ))}
        
      </div>
      <div className="bottom-buttons-bio">
        <button
          className="bottom-button"
          onClick={() => {
            setLeaveNotAllowed(false);
            setTimeout(() => {
              navigate(`/reunion/${reunionID}`);
            }, 0);
          }}>
          Cancel Edits
        </button>
        <button 
          className="bottom-button" 
          onClick={handleSave}>
            Save Bio
          </button>
      </div>
      
      {imagePickerIndex !== null && (
        <div
          className="image-picker-backdrop"
          onMouseDown={closeImagePicker}
        >
          <div
            className="image-picker-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="image-picker-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="image-picker-header">
              <h2 id="image-picker-title">
                Choose an uploaded image
              </h2>

              <button
                type="button"
                className="image-picker-close"
                aria-label="Close image picker"
                onClick={closeImagePicker}
              >
                ×
              </button>
            </div>

            <div className="image-picker-filters">
              <div className="image-picker-row">
                <label htmlFor="uploaded-by-filter">
                  Uploaded By:
                </label>
                <select
                  id="uploaded-by-filter"
                  value={uploadedByFilter}
                  onChange={(e) => setUploadedByFilter(e.target.value)}
                >
                  <option value="all" className="all-images">
                    All Images
                  </option>
                  <option value={loginID} className="my-images">
                    My Images
                  </option>
                  {sortedPeopleOptions.filter(([id]) => !id.startsWith("P0")).map(([id, person]) => (
                    <option
                      key={id}
                      value={id}
                    >
                      {person.AKA || `${person.f_name} ${person.l_name}`}
                    </option>
                  ))}
                </select>
              </div>

              <div className="image-picker-row">
                <label htmlFor="sort-field">
                  Sort By:
                </label>
                <select
                  id="sort-field"
                  value={sortField}
                  onChange={(e) => setSortField(e.target.value)}
                >
                  <option value="saved_date">
                    Upload Date
                  </option>

                  <option value="date">
                    Photo Date
                  </option>
                </select>

                <select
                  id="sort-direction"
                  value={sortDirection}
                  onChange={(e) => setSortDirection(e.target.value)}
                >
                  <option value="desc">
                    Newest First
                  </option>
                  <option value="asc">
                    Oldest First
                  </option>
                </select>
              </div>
            </div>
              
            <div className="existing-image-grid-container">
              {pickerLoading && (
                <div>Loading images...</div>
              )}
              {pickerError && (
                <div>Error: {pickerError}</div>
              )}
              {!pickerLoading && !pickerError && (
                <div className="existing-image-grid">
                  {pickerImages.map((image) => {
                    const imageSrc = image.media_id;
                    const imageAlt = image.subtext || "Uploaded image";
                    const isSelected =
                      blocks[imagePickerIndex]?.src === imageSrc;
                    return (
                      <button
                        key={imageSrc}
                        type="button"
                        className={
                          isSelected
                            ? "existing-image-option selected"
                            : "existing-image-option"
                        }
                        onClick={() =>
                          chooseExistingImage(imageSrc, image)
                        }
                      >
                        <BlockImg
                          imageSrc={imageSrc}
                          alt={imageAlt}
                          className="existing-image-thumbnail"
                        />

                        {image.subtext && (
                          <span className="existing-image-caption">
                            {image.subtext}
                            {/* {image.saved_date.slice(5,)} {image.date?.year?? ""} */}
                          </span>
                        )}
                      </button>
                    );
                  })}

                  {pickerImages.length === 0 && (
                    <p className="no-existing-images">
                      No images uploaded by {people[uploadedByFilter]?.AKA?.split(' ')[0]?? people[uploadedByFilter]?.f_name}
                    </p>
                  )}

                </div>
              )}

            </div>
          </div>
        </div>
      )}

    </div>
  )
}


function markdownToBlocks(md) {
  const blocks = [];
  
  if (!md) {
    blocks.push({ id:crypto.randomUUID(), type: "p", content: "" });
    return blocks;
  }

  const lines = md.split("\n");

  lines.forEach(line => {
    if (line.startsWith("## ")) {
      blocks.push({ id:crypto.randomUUID(), type: "h2", content: line.replace("## ", "") });
      return;
    }
    // Detect image WITH trailing text
    const imgMatch = line.match(/!\[(.*?)\]\((.*?)(?:\s+"(.*?)")?\)\s*(.*)/);

    if (imgMatch) {
      const alt = imgMatch[1];
      const src = imgMatch[2];
      const title = imgMatch[3];
      const trailingText = imgMatch[4]?.trim();

      blocks.push({ id:crypto.randomUUID(), type: "img", src, alt, title })
      if (trailingText) {
        blocks.push({ id:crypto.randomUUID(), type: "p", content: trailingText });
      }
      return;
    }
    if (line.trim() !== "") {
      blocks.push({ id:crypto.randomUUID(), type: "p", content: line });
    }
  });

  return blocks;
}

function blocksToMarkdown(blocks) {
  return blocks.map(b => {
    if (b.type === "h2") return `## ${b.content}\n`;
    if (b.type === "img") return `![${b.alt}](${b.src} "${b.title}")`;
    if (b.type === "p") return `${b.content}\n\n`;
    return "";
  }).join("");
}

function BlockImg({imageSrc, alt, className}) {
  const {data: url} = useMediaUrl(imageSrc);
  const blockImg = url ? `${url}&block=1` : null;

  return(
    <>
      {blockImg && (
        <img src={blockImg} alt={alt} className={className}/>
      )}
    </>
  )
}